/**
 * Visitor-facing street line.
 *
 * MLS StreetNumber 0 / 00 is a placeholder, not a house number. Do not
 * publish it. Keep the street name when one exists.
 *
 * Founding case: /cities/bend/awbrey-butte cards printed 0 Moonshadow Court
 * for MLS 220221237 / 220221242 / 220221243 (fleet 3545811a84a2445587694783602cebc1).
 */

export function publishStreetNumber(raw: string | number | null | undefined): string | null {
  if (raw == null) return null
  const value = String(raw).trim()
  if (!value) return null
  if (/^0+$/.test(value)) return null
  return value
}

export function publishStreetPart(raw: string | null | undefined): string | null {
  const value = raw?.trim() ?? ''
  return value || null
}

/**
 * USPS street suffixes the feed carries, full form to abbreviation. A name and
 * a suffix that spell the same word two ways are one suffix.
 */
const STREET_SUFFIX_ABBREVIATIONS: Record<string, string> = {
  alley: 'aly',
  avenue: 'ave',
  boulevard: 'blvd',
  circle: 'cir',
  court: 'ct',
  cove: 'cv',
  crossing: 'xing',
  drive: 'dr',
  expressway: 'expy',
  freeway: 'fwy',
  highway: 'hwy',
  hollow: 'holw',
  lane: 'ln',
  loop: 'loop',
  parkway: 'pkwy',
  path: 'path',
  pike: 'pike',
  place: 'pl',
  point: 'pt',
  ridge: 'rdg',
  road: 'rd',
  square: 'sq',
  street: 'st',
  terrace: 'ter',
  trail: 'trl',
  view: 'vw',
  way: 'way',
}

/** "Boulevard", "Blvd", "Blvd." all read as "blvd". */
function canonicalStreetSuffix(word: string): string {
  const bare = word.trim().replace(/\.$/, '').toLowerCase()
  return STREET_SUFFIX_ABBREVIATIONS[bare] ?? bare
}

/**
 * True when the street name already ends with this suffix, in either spelling:
 * Drive Drive, and Silver Lake Blvd + Boulevard (W8 QA: the homepage card read
 * "1295 Silver Lake Blvd Boulevard").
 */
export function streetNameHasSuffix(streetName: string, streetSuffix: string): boolean {
  const name = streetName.trim()
  const suffix = streetSuffix.trim()
  if (!name || !suffix) return false
  const words = name.split(/\s+/)
  const last = words[words.length - 1] ?? ''
  if (last.localeCompare(suffix, undefined, { sensitivity: 'accent' }) === 0) return true
  // A one-word name is the street's own name, not a suffix already joined on.
  if (words.length < 2) return false
  return canonicalStreetSuffix(last) === canonicalStreetSuffix(suffix)
}

/** True when the street name already starts with this directional (NW NW). */
export function streetNameHasDir(streetName: string, dir: string): boolean {
  const name = streetName.trim()
  const prefix = dir.trim()
  if (!name || !prefix) return false
  const first = name.split(/\s+/)[0] ?? ''
  return first.localeCompare(prefix, undefined, { sensitivity: 'accent' }) === 0
}

export function publishStreetLine(input: {
  streetNumber?: string | number | null
  streetDirPrefix?: string | null
  streetName?: string | null
  streetSuffix?: string | null
  streetDirSuffix?: string | null
}): string | null {
  const name = publishStreetPart(input.streetName)
  const suffix = publishStreetPart(input.streetSuffix)
  const dirPrefix = publishStreetPart(input.streetDirPrefix)
  const dirSuffix = publishStreetPart(input.streetDirSuffix)
  const line = [
    publishStreetNumber(input.streetNumber),
    name && dirPrefix && streetNameHasDir(name, dirPrefix) ? null : dirPrefix,
    name,
    name && suffix && streetNameHasSuffix(name, suffix) ? null : suffix,
    dirSuffix,
  ]
    .filter((part): part is string => part != null)
    .join(' ')
    .trim()
  return line || null
}

type ListingStreetBits = {
  streetNumber?: string | number | null
  streetDirPrefix?: string | null
  streetName?: string | null
  streetSuffix?: string | null
  streetDirSuffix?: string | null
  city?: string | null
  postalCode?: string | null
}

/** Street line for listing detail (never a leading placeholder 0). */
export function listingMlsStreetLine(listing: ListingStreetBits): string {
  return publishStreetLine(listing) ?? ''
}

/**
 * Street, city, OR postal. Comma between street and city — the comma-less
 * form matched no county/Zillow record (design-audit P1, trust).
 */
export function listingMlsAddressFull(listing: ListingStreetBits): string {
  const street = listingMlsStreetLine(listing)
  return [street, listing.city ? `${listing.city}, OR` : '', listing.postalCode ?? '']
    .filter(Boolean)
    .join(', ')
    .replace(/, OR,\s/, ', OR ')
    .trim()
}

/**
 * Card / row address (Matt 2026-08-27): every card that names a home names its
 * CITY — "714 Wrangler Court, Sisters" — because cards travel: open houses,
 * trails, price drops, and search rows all mix cities, and a bare street line
 * made the reader guess. No ", OR" and no zip on a card; those belong to the
 * detail page (listingMlsAddressFull).
 */
export function publishCardAddress(listing: ListingStreetBits): string {
  const street = listingMlsStreetLine(listing)
  const city = listing.city?.trim() ?? ''
  if (!street) return city
  return city ? `${street}, ${city}` : street
}

/** Already-joined MLS line. Strip a leading placeholder 0. */
export function publishUnparsedStreetLine(raw: string | null | undefined): string | null {
  const value = raw?.trim() ?? ''
  if (!value) return null
  const stripped = value.replace(/^0+\s+/, '').trim()
  return stripped || null
}
